import { useState, useEffect, useRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

/**
 * PartyAutocomplete — typeahead input that fetches normalized (punctuation-agnostic,
 * case-insensitive) matches of party/customer names from the master collection and
 * recent entries. Prevents duplicates caused by trailing commas, hyphens, etc.
 */
export default function PartyAutocomplete({
  value = "",
  onChange,
  label = "Party Name *",
  placeholder = "Start typing party name...",
  masterType = "parties",
  required = false,
  testId = "field-party-autocomplete",
  className = "",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const normalizedValue = useMemo(() => (value || "").replace(/[^a-zA-Z0-9]+/g, "").toLowerCase(), [value]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/masters/autocomplete/${masterType}`, {
          params: { q: value || "", limit: 15 },
        });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [value, open, masterType]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (name) => { onChange?.(name); setOpen(false); setHighlight(-1); };

  // Detect if current typed value is a potential duplicate of an existing suggestion
  const duplicateMatch = useMemo(() => suggestions.find((s) => {
    const sn = (s || "").replace(/[^a-zA-Z0-9]+/g, "").toLowerCase();
    return sn && normalizedValue && sn === normalizedValue && s.trim().toLowerCase() !== (value || "").trim().toLowerCase();
  }), [suggestions, normalizedValue, value]);

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(suggestions.length - 1, h + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); return; }
    if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); return; }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      {label && <Label className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-1.5 block">{label}</Label>}
      <Input
        value={value || ""}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-11"
        required={required}
        data-testid={testId}
        autoComplete="off"
      />
      {duplicateMatch && (
        <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid="dup-warning">
          <span>⚠</span> Similar existing record: <button type="button" onClick={() => pick(duplicateMatch)} className="font-semibold underline hover:text-amber-900">&ldquo;{duplicateMatch}&rdquo;</button>. Reuse it to avoid duplicates?
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, idx) => (
            <li
              key={s}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(idx)}
              className={`px-3 py-2 text-sm cursor-pointer ${highlight === idx ? "bg-blue-50 text-blue-900 font-medium" : "hover:bg-slate-50"}`}
              data-testid={`${testId}-option-${idx}`}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
